//Modal shown when mint button is pressed by issuer. When issuer enters amount to be minted the mint API is called.
import React, { useState, ChangeEvent, useEffect, useCallback } from 'react';
import {
  makeStyles,
  Theme,
  createStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { mintBolt } from '../../redux/actions/boltAction';
import { selectIsDelegate, selectActingID } from '../../redux/selectors';
import ActionButton from '../common/ActionButton';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    boltViewPaper: {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.secondary.main,
      minHeight: '500px',
      minWidth: '400px',
      alignItems: 'center',
      padding: 10,
    },
    boltViewContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
    },
    buttonSection: {
      display: 'flex',
      alignItems: 'center',
      position: 'absolute',
      justifyContent: 'center',
      bottom: 0,
      paddingBottom: 20,
      width: '100%',
    },
    actionButton: {
      margin: 5,
      padding: '10px 15px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      transition: 'background-color 0.5s',
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: theme.palette.grey[200],
      },
    },
    textFieldStyle: {
      width: 300,
      display: 'block',
    },
  })
);

//Type of the props
type MINTBOLTS = {
  setDialogOpen: (isOpen: boolean) => void;
  handleBack: () => void;
  specID: string;
};

// FIXLATER
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function MintBolt(props: MINTBOLTS) {
  const classes = useStyles();
  const [amount, setAmount] = useState('');
  const [amountValid, setAmountValid] = useState(true);
  const setDialogOpen = props.setDialogOpen;
  const handleBack = props.handleBack;
  const specID = props.specID;
  const dispatch = useDispatch();
  const isDelegate = useSelector(selectIsDelegate);
  const actingID = useSelector(selectActingID);
  const [isMinting, setIsMinting] = React.useState<boolean | null>(null);

  //Close the dialog
  const handleClose = useCallback(() => {
    setDialogOpen(false);
  }, [setDialogOpen]);

  //Handles input amount
  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value);
    if (isNaN(Number(e.target.value))) {
      setAmountValid(false);
    } else {
      setAmountValid(true);
    }
  };

  // Handles the submit button and calls the service to mint bolt
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsMinting(true);
    dispatch(
      mintBolt(
        specID,
        Number(amount),
        setIsMinting,
        isDelegate ? actingID : undefined
      )
    );
  };

  useEffect(() => {
    if (isMinting === false) {
      handleClose();
    }
  }, [isMinting, handleClose]);

  return (
    <Dialog
      classes={{
        paper: classes.boltViewPaper,
      }}
      open={true}
      onClose={handleClose}
      maxWidth={'md'}
    >
      <DialogTitle id="form-dialog-title">Mint </DialogTitle>
      <DialogContent className={classes.boltViewContent}>
        <form onSubmit={handleSubmit}>
          <TextField
            className={classes.textFieldStyle}
            required
            variant="outlined"
            color="secondary"
            margin="normal"
            fullWidth
            id="amount"
            label="Amount"
            name="amount"
            autoFocus
            value={amount}
            onChange={handleAmountChange}
            error={!amountValid}
            helperText={amountValid ? '' : 'Spec ID cannot contain spaces'}
            disabled={isMinting === true}
          />
        </form>
        {isMinting && (
          <div style={{ textAlign: 'center' }}>
            <div>Please wait as we mint your ZUZ...</div>
            <CircularProgress />
          </div>
        )}
      </DialogContent>
      <ActionButton
        handleCallback1={handleBack}
        handleCallback2={handleSubmit}
        name1="Back"
        name2="Submit"
        disabled2={!amountValid}
      />
    </Dialog>
  );
}

export default MintBolt;
